/**
 * Cron Utilities
 *
 * Drives the friendly Schedule picker used by both `RoutineForm` (direct
 * UI creation/edit) and the in-chat `RoutineProposalCard` previews.
 *
 * Compiles a high-level `SchedulePickerState` (frequency + time-of-day +
 * day selectors) into a standard 5-field cron expression, parses common
 * patterns back, and provides human-readable + next-N-runs previews.
 *
 * @see docs/ROUTINES_REDESIGN_PLAN.md §6.3
 */

import cronstrue from 'cronstrue';
import { CronExpressionParser } from 'cron-parser';

export type SchedulePickerKind = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

export interface SchedulePickerState {
  kind: SchedulePickerKind;
  /** 0-23, used for daily/weekly/monthly */
  hour: number;
  /** 0-59, used for all kinds (hourly uses minute only) */
  minute: number;
  /** 0-6 (Sun=0), used for weekly */
  daysOfWeek: number[];
  /** 1-31, used for monthly */
  dayOfMonth: number;
  /** Raw cron expression, used for custom (and as override target) */
  customCron: string;
}

export const DEFAULT_SCHEDULE_PICKER_STATE: SchedulePickerState = {
  kind: 'daily',
  hour: 9,
  minute: 0,
  daysOfWeek: [1],
  dayOfMonth: 1,
  customCron: '0 9 * * *',
};

export function compilePickerToCron(state: SchedulePickerState): string {
  switch (state.kind) {
    case 'hourly':
      return `${state.minute} * * * *`;
    case 'daily':
      return `${state.minute} ${state.hour} * * *`;
    case 'weekly': {
      const dow = state.daysOfWeek.length ? [...state.daysOfWeek].sort().join(',') : '1';
      return `${state.minute} ${state.hour} * * ${dow}`;
    }
    case 'monthly':
      return `${state.minute} ${state.hour} ${state.dayOfMonth} * *`;
    case 'custom':
      return state.customCron;
  }
}

/**
 * Best-effort parse of a 5-field cron back into picker state. Falls back
 * to `kind: 'custom'` for anything that doesn't match the picker's emitted
 * shapes (so the user always sees something editable, even for cron
 * expressions written outside the picker).
 */
export function parseCronToPicker(cron: string): SchedulePickerState {
  const fallback: SchedulePickerState = {
    ...DEFAULT_SCHEDULE_PICKER_STATE,
    kind: 'custom',
    customCron: cron,
  };

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return fallback;

  const [minStr, hourStr, domStr, monStr, dowStr] = parts;
  const minute = Number(minStr);
  const hour = Number(hourStr);

  if (Number.isNaN(minute) || minute < 0 || minute > 59) return fallback;

  const isStar = (s: string): boolean => s === '*';

  // Hourly: M * * * *
  if (isStar(hourStr) && isStar(domStr) && isStar(monStr) && isStar(dowStr)) {
    return { ...DEFAULT_SCHEDULE_PICKER_STATE, kind: 'hourly', minute, customCron: cron };
  }

  if (Number.isNaN(hour) || hour < 0 || hour > 23) return fallback;

  // Daily: M H * * *
  if (isStar(domStr) && isStar(monStr) && isStar(dowStr)) {
    return { ...DEFAULT_SCHEDULE_PICKER_STATE, kind: 'daily', hour, minute, customCron: cron };
  }

  // Weekly: M H * * D[,D,...] (only standalone numbers, no ranges/steps)
  if (isStar(domStr) && isStar(monStr) && /^\d+(,\d+)*$/.test(dowStr)) {
    const days = dowStr.split(',').map(Number).filter(d => d >= 0 && d <= 6);
    if (days.length > 0) {
      return {
        ...DEFAULT_SCHEDULE_PICKER_STATE,
        kind: 'weekly',
        hour,
        minute,
        daysOfWeek: days,
        customCron: cron,
      };
    }
  }

  // Monthly: M H D * *
  const dom = Number(domStr);
  if (!Number.isNaN(dom) && dom >= 1 && dom <= 31 && isStar(monStr) && isStar(dowStr)) {
    return {
      ...DEFAULT_SCHEDULE_PICKER_STATE,
      kind: 'monthly',
      hour,
      minute,
      dayOfMonth: dom,
      customCron: cron,
    };
  }

  return fallback;
}

export function humanReadableCron(cron: string): string {
  try {
    return cronstrue.toString(cron, { use24HourTimeFormat: false });
  } catch {
    return cron;
  }
}

export function nextNRuns(cron: string, timezone: string, n = 3): Date[] {
  try {
    const it = CronExpressionParser.parse(cron, { tz: timezone });
    const out: Date[] = [];
    for (let i = 0; i < n; i++) {
      out.push(it.next().toDate());
    }
    return out;
  } catch {
    return [];
  }
}

export function isValidCron(cron: string): boolean {
  try {
    CronExpressionParser.parse(cron);
    return true;
  } catch {
    return false;
  }
}
